import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { Spin, Empty, Icon, Button, Modal, Input, message, Alert, Popover, Typography, Checkbox, Tooltip } from "antd";
import ChargeSection from "./ChargeSection";
import Button_v2 from "../../../stories/components/Button_v2";
import { alertMessage } from "../../../common/Common";
import { renderAlertMessage } from "../../../helpers/common";
import { createReBill, onConfirmAndSaveAllCharges } from "../../../api/Billing";
import AccessorialChargeSection from "./AccessorialChargeSection";
import { UserContext } from "../../../context/UserContext";
import { OrgContext } from "../../../context/OrgContext";
import LabelInfo from "../../configurations/LabelInfo";
import { get } from "lodash";

const getOrderNumber = order =>
  order && (order.customer_order_number || order.order_number || order.id || order._id);

const typeMap = { P: "PICKUP", D: "DELIVERY", T: "TRANSFER" };

const getOrderType = order => {
  if (!order) return "";
  return typeMap[order.type_of_order || order.customer_order_type] || "ORDER";
};

const getHawb = order => {
  if (!order) return "N/A";
  const hawb = order.hawb || "N/A";
  return hawb.length > 15 ? `${hawb.slice(0, 15)}...` : hawb;
};

const getFullHawb = order => {
  if (!order) return "N/A";
  return order.hawb || "N/A";
};

const SECTION_CONFIG = [
  { key: "transportation_charges", title: "CHARGES" },
  { key: "fuel_surcharges", title: "FUEL SURCHARGES" },
  { key: "accessorial_charges", title: "ACCESSORIAL CHARGES" },
  { key: "adjustment_charges", title: "ADJUSTMENT CHARGES" },
];

const buildSectionsFromCharges = chargesDetails => {
  const source = (chargesDetails && chargesDetails.charges) || chargesDetails || {};

  return SECTION_CONFIG.map(({ key, title }) => {
    const block = source[key];
    if (!block) {
      return { key, title, subtotal: 0, lines: [] };
    }
    return {
      key,
      title,
      subtotal: Number(block.total_amount || 0),
      lines: block.invoice_lines || [], // raw, untouched
    };
  });
};

const buildInfoMessages = orderDetails => {
  const editBlockReasons = (orderDetails && orderDetails.edit_block_reasons) || [];
  const settlementWarnings = (orderDetails && orderDetails.settlement_warnings) || [];
  return [...editBlockReasons, ...settlementWarnings];
};

const MAX_REMARK_LENGTH = 500;

const RightOrderDetailsPanel = forwardRef(
  (
    {
      order = null,
      orderDetails = null,
      chargesDetails = null,
      loading = false,
      accountInvoiceId = null,
      isEditMode = false,
      onEnableEditMode = () => {},
      accessorialList = [],
      displayComponents = [],
      orgComponents = [],
      refreshCharges = () => {},
      onPendingChangesChange = () => {},
      setHasInvalidCharges = () => {},
      drivers = [],
      isFetchingDetails = false,
      onOrderAmountUpdated = () => {}, 
      onChargesSaved = () => {}, // NEW
      setRebillingProgress = () => {},
      rebillingProgress = false
    },
    ref
  ) => {
  const userContext = useContext(UserContext);
  const orgContext = useContext(OrgContext);

  const [pendingCreates, setPendingCreates] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [sectionValidation, setSectionValidation] = useState({});
  const invalidCharges = SECTION_CONFIG.some(({ key }) => sectionValidation[key]);
  const createIndexRef = useRef({});
  const updateIndexRef = useRef({});
  const remarkInputRef = useRef(null);
  // Re-bill popover state
  const [isRebillPopoverVisible, setIsRebillPopoverVisible] = useState(false);
  const [rebillFormData, setRebillFormData] = useState({
    description: "",
    charge_back_to_drivers: "false",
  });
  const [isCreatingRebill, setIsCreatingRebill] = useState(false);
  const [currentReBillPaymentDetails, setCurrentReBillPaymentDetails] = useState(null);
  //..

  const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);
  const [remark, setRemark] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  // "manual" -> opened via the Save Order Charges button
  // "switch" -> opened because the user chose "Save" from the Unsaved Changes guard
  const [remarkModalIntent, setRemarkModalIntent] = useState("manual");
  const pendingSwitchResolveRef = useRef(null);

  const { orgSettings: organizationSettings, glCodesList, currentOrg } = orgContext || {};
  const orgrules = _.get(userContext, "orgRules.org_billing_rules", {});
  const groupGlByWarehouseSetting = _.get(orgrules, "group_gl_by_warehouse") === "true";

  const { total_amount, balance_amount, is_rebill_completed, is_file_generated } = order || {};
  const {is_rebill_order} = orderDetails || {};

  const totalAmount = _.toNumber(total_amount);
  const balanceAmount = _.toNumber(balance_amount);

  const isRebillCompleted = is_rebill_completed === true || is_rebill_completed === "true" || is_rebill_order === true || is_rebill_order === "true";
  const isFileGenerated = is_file_generated === true || is_file_generated === "true";
  const isOrderLocked =
    (totalAmount !== 0 && totalAmount !== balanceAmount) || isRebillCompleted || isFileGenerated;

  const editable = isEditMode && !isOrderLocked;

  const hasPendingChanges =
    pendingCreates.length > 0 || pendingUpdates.length > 0 || pendingDeletes.length > 0;
  
  const isApprovedInvoice = orderDetails?.invoice_status === "APPROVED" && orderDetails?.ar_status === "OPEN";
  const showRebillButton = isEditMode && isApprovedInvoice && !isRebillCompleted;


    // Let the parent container know whenever pending-change state changes,
    // so it can decide whether switching orders needs to be guarded.
  useEffect(() => {
    onPendingChangesChange(hasPendingChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingChanges]);

  // AutoFocus the textarea of remark modal
  useEffect(() => {
    if (isRemarkModalVisible) {
      setTimeout(() => {
        if (remarkInputRef.current) {
          remarkInputRef.current.focus();
        }
      }, 0);
    }
  }, [isRemarkModalVisible]);

  useEffect(() => {
    setHasInvalidCharges(invalidCharges);
  }, [invalidCharges, setHasInvalidCharges]);

  useEffect(() => {
    setSectionValidation({});
  }, [order?.customer_order_id]);

  const handleSectionValidationChange = useCallback((sectionKey, hasErrors) => {
    setSectionValidation(prev => {
      if (prev[sectionKey] === hasErrors) return prev; // bail out if unchanged
      return { ...prev, [sectionKey]: hasErrors };
    });
  }, []);

  const resetPendingChanges = () => {
    setPendingCreates([]);
    setPendingUpdates([]);
    setPendingDeletes([]);

    createIndexRef.current = {};
    updateIndexRef.current = {};
  };

    const buildChargesPayload = currentRemark => ({
      source: "INVOICE",
      account_invoice_id: accountInvoiceId,
      customer_order_id: orderDetails?.customer_order_id || order?.customer_order_id,
      organization_id: currentOrg?.id,
      order_group_type: orderDetails?.order_group_type,
      remark: currentRemark.trim(),
      reference_id:
        orderDetails?.order_group_type === "ORDER"
          ? orderDetails?.customer_order_id
          : orderDetails?.customer_order_number,
      status: "APPROVED",
      creates: pendingCreates,
      updates: pendingUpdates,
      deletes: pendingDeletes,
    });

    // Core save routine, shared by the manual "Save Order Charges" flow
    // and the "Unsaved Changes" switch-order guard flow.
    const performSave = async currentRemark => {
      const chargesPayload = buildChargesPayload(currentRemark);
      const response = await onConfirmAndSaveAllCharges(chargesPayload);

       if (response?.success) {
        const totalUpdatedAmount = response?.billedAmount || 0;
        const billedOrderId = response?.billedOrderId || "";
        alertMessage(response.message || "Remark updated successfully.");
        resetPendingChanges();

        // NEW: notify parent so LeftOrdersPanel amount updates immediately
        onOrderAmountUpdated(billedOrderId, totalUpdatedAmount);
        onChargesSaved(); // NEW - tell container something actually changed on the server

        if (remarkModalIntent === "manual") {
          // in current page save case
          refreshCharges();
        }
        return true;
      }

      renderAlertMessage(response?.errors || ["Failed to update remark and save charges."]);
      return false;
    };

    // Exposed to the parent container so it can drive the "Save" action
    // from the Unsaved Changes guard modal.
    useImperativeHandle(ref, () => ({
      hasPendingChanges: () => hasPendingChanges,
      discardPendingChanges: () => {
        resetPendingChanges();
      },
      // Opens the remark modal in "switch" mode; resolves the returned
      // promise with true/false once the user finishes (or cancels).
      requestSaveForSwitch: () =>
        new Promise(resolve => {
          pendingSwitchResolveRef.current = resolve;
          setRemark("");
          setRemarkModalIntent("switch");
          setIsRemarkModalVisible(true);
        }),
    }));

  if (!order) {
    return (
      <div className="right-order-details-panel right-order-details-panel--empty">
        <Empty description="Select an order to view its charges" />
      </div>
    );
  }

  const sections = buildSectionsFromCharges(chargesDetails);
  const infoMessages = buildInfoMessages(orderDetails);
  const billTotal = Number(
    (orderDetails && orderDetails.total_amount) || order.total_amount || order.balance_amount || 0
  );

  const handleLineAdded = (sectionKey, lineKey, finalizedLine) => {
    const trackKey = `${sectionKey}::${lineKey}`;
    const payloadLine = {
      invoice_type: finalizedLine.invoice_type,
      invoice_name: finalizedLine.invoice_name,
      invoice_value: Number(finalizedLine.invoice_value) || 0,
      invoice_quantity: Number(finalizedLine.invoice_quantity) || 1,
      location_id: finalizedLine.location_id,
      location_type: finalizedLine.location_type,
      gl_code: finalizedLine.gl_code,
      accessorial_code: finalizedLine.accessorial_code || undefined,
      account_accessorial_id: finalizedLine.account_accessorial_id || undefined,
      component_params: finalizedLine.component_params || undefined,
      driver_pay_params: finalizedLine.driver_pay_params || undefined,
      apply_driver_pay_for_adjustment: finalizedLine.apply_driver_pay_for_adjustment,
      is_modified: false,
      added_from: "MANUAL",
    };

    setPendingCreates(prev => {
      const idx = createIndexRef.current[trackKey];
      if (idx !== undefined) {
        const next = [...prev];
        next[idx] = payloadLine;
        return next;
      }
      createIndexRef.current[trackKey] = prev.length;
      return [...prev, payloadLine];
    });
  };

  const handleLineEdited = (sectionKey, lineKey, updatedLine) => {
    const trackKey = `${sectionKey}::${lineKey}`;
    const payloadLine = {
      invoice_line_id: updatedLine.id || updatedLine.invoice_line_id || "",
      invoice_type: updatedLine.invoice_type,
      invoice_name: updatedLine.invoice_name,
      invoice_value: Number(updatedLine.invoice_value) || 0,
      invoice_quantity: Number(updatedLine.invoice_quantity) || 1,
      location_id: updatedLine.location_id,
      location_type: updatedLine.location_type,
      old_location_id: updatedLine.old_location_id,
      old_location_type: updatedLine.old_location_type,
      gl_code: updatedLine.gl_code,
      added_from: updatedLine.added_from,
      is_modified: updatedLine.is_modified,
    };

    setPendingUpdates(prev => {
      const idx = updateIndexRef.current[trackKey];
      if (idx !== undefined) {
        const next = [...prev];
        next[idx] = payloadLine;
        return next;
      }
      updateIndexRef.current[trackKey] = prev.length;
      return [...prev, payloadLine];
    });
  };

  const handleLineDeleted = (sectionKey, lineKey, line, wasNeverAdded = false) => {
    const trackKey = `${sectionKey}::${lineKey}`;

    // Was an unsaved create (or never confirmed) -> just drop it, nothing to delete server-side
      if (wasNeverAdded || createIndexRef.current[trackKey] !== undefined) {
      const idx = createIndexRef.current[trackKey];
      if (idx !== undefined) {
        setPendingCreates(prev => prev.filter((_, i) => i !== idx));
        delete createIndexRef.current[trackKey];
        // shift down any indices after the removed one
        Object.keys(createIndexRef.current).forEach(k => {
          if (createIndexRef.current[k] > idx) createIndexRef.current[k] -= 1;
        });
      }
      return;
    }

    // Drop any pending edit tracked for this line too
    const uIdx = updateIndexRef.current[trackKey];
    if (uIdx !== undefined) {
      setPendingUpdates(prev => prev.filter((_, i) => i !== uIdx));
      delete updateIndexRef.current[trackKey];
      Object.keys(updateIndexRef.current).forEach(k => {
        if (updateIndexRef.current[k] > uIdx) updateIndexRef.current[k] -= 1;
      });
    }

    const lineId = line.id || line._id;
    if (lineId) {
      setPendingDeletes(prev => [...prev, { invoice_line_id: lineId }]);
    }
  };

  const handleResetEditing = () => {
    resetPendingChanges();
    refreshCharges();
  }

  const openRemarkModal = () => {
    setRemark("");
    setRemarkModalIntent("manual");
    setIsRemarkModalVisible(true);
  };

  const closeRemarkModal = () => {
    if (isConfirming) return;

    // If this modal was opened as part of a "switch order" save request
    // and the user backs out without saving, resolve as "not saved".
    if (remarkModalIntent === "switch" && pendingSwitchResolveRef.current) {
        pendingSwitchResolveRef.current(false);
        pendingSwitchResolveRef.current = null;
      }

    setIsRemarkModalVisible(false);
    setRemark("");
    setRemarkModalIntent("manual");
  };

  const handleConfirmAndSaveRemark = async () => {
    const trimmed = remark.trim();
    if (!trimmed) return;

    setIsConfirming(true);

    try {
      if (hasPendingChanges) {
        const success = await performSave(trimmed);

          if (success) {
          setIsRemarkModalVisible(false);
          setRemark("");

          if (remarkModalIntent === "switch" && pendingSwitchResolveRef.current) {
            pendingSwitchResolveRef.current(true);
            pendingSwitchResolveRef.current = null;
          }
          setRemarkModalIntent("manual");
        } else if (remarkModalIntent === "switch" && pendingSwitchResolveRef.current) {
            // Save failed — keep the modal open so the user can retry,
            // and resolve as "not saved" only if they eventually cancel.
          }
        } else {
          onEnableEditMode(false);
          setIsRemarkModalVisible(false);
          setRemark("");
          setRemarkModalIntent("manual");
        }
      } catch (err) {
        console.error("Failed to update remark and save charges.", err);
        renderAlertMessage(["Failed to update remark and save charges. Please try again."]);
      } finally {
        setIsConfirming(false);
    }
  };

  // Re-bill popover handlers
  const resetRebillForm = () => {
    setRebillFormData({ description: "", charge_back_to_drivers: "false" });
  };

  const closeRebillPopover = () => {
    if (isCreatingRebill) return;
    setIsRebillPopoverVisible(false);
    resetRebillForm();
  };

  const handleRebilling = async () => {
    setIsCreatingRebill(true);
    setRebillingProgress(true)
    try {
      const payload = {
        customer_order_id: orderDetails?.customer_order_id || order?.customer_order_id,
        account_invoice_id: accountInvoiceId,
        organization_id: currentOrg?.id,
        description: rebillFormData.description,
        charge_back_to_drivers: rebillFormData.charge_back_to_drivers,
        screen: "INVOICE",
      };

      const response = await createReBill(payload);

      if (response?.success) {
        const reBillPaymentDetails = response.paymentDetails || {};
        const {
          credit_amount = "0",
          credit_number = "",
          adjustment_amount = "0",
          customer_order_number = "",
          charge_back_to_drivers = "false",
        } = reBillPaymentDetails;

        setCurrentReBillPaymentDetails({
          currentBillingOrder: customer_order_number,
          currentCreditNo: credit_number,
          creditAmount: credit_amount,
          adjustmentAmount: adjustment_amount,
          charge_back_to_drivers,
        });

        alertMessage(response.message || "Rebill created successfully.");
        refreshCharges();
        setIsRebillPopoverVisible(false);
        resetRebillForm();
      } else if (response?.errors) {
        renderAlertMessage(response.errors);
      }
    } catch (error) {
      console.error("error in creating rebill", error);
      renderAlertMessage(["Failed to create rebill. Please try again."]);
    } finally {
      setIsCreatingRebill(false);
      setRebillingProgress(false);
    }
  };

  return (
    <div className="right-order-details-panel">
      <Spin spinning={loading || isFetchingDetails} size="small" tip="Loading order details...">
        <div
          className={`right-order-details-panel__content${
            isCreatingRebill ? " right-order-details-panel__content--blurred" : ""
          }`}
        >
        <div className="right-order-details-panel__header">
          <div>
            <div className="right-order-details-panel__order-title">
              ORDER {getOrderNumber(orderDetails || order)}
            </div>
            <div className="right-order-details-panel__order-meta">
              HAWB:{" "}
              {getHawb(orderDetails || order).length > 15 ? (
                <Tooltip title={getFullHawb(orderDetails || order)}>
                  <span>{getHawb(orderDetails || order)}</span>
                </Tooltip>
              ) : (
                getHawb(orderDetails || order)
              )}
              &nbsp;|&nbsp;
              Order Type: {getOrderType(orderDetails || order)} &nbsp;|&nbsp;
              Account: {(orderDetails || order).account_name || "N/A"}
            </div>
          </div>
          <div className="invoice-charges-header-actions">
            {showRebillButton &&
              (hasPendingChanges || invalidCharges ? (
                <Popover
                  content={
                    invalidCharges
                      ? "Please fix the invalid charges before exiting edit mode."
                      : "You have unsaved changes. Please save or discard them before exiting edit mode."
                  }
                  trigger="hover"
                >
                  <span>
                    <Button_v2
                      icon="money-collect"
                      key="rebill-order"
                      size="medium"
                      className="small-btn tealButton"
                      text="Rebill"
                      disabled
                      style={{ marginRight: -15 }}
                    />
                  </span>
                </Popover>
              ) : (
                <Popover
                  content={
                    <Spin spinning={isCreatingRebill} size="small" tip="Creating Rebill...">
                      <div style={{ width: 300 }}>
                        <Typography.Text strong>Add Re-Bill Description</Typography.Text>
                        <Input.TextArea
                          placeholder="Enter description"
                          value={rebillFormData.description}
                          onChange={e => {
                            const value = e.target && e.target.value;
                            setRebillFormData(prev => ({
                              ...prev,
                              description: value,
                            }));
                          }}
                          rows={2}
                          style={{ marginTop: 8, marginBottom: 8 }}
                        />
                        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                          <Checkbox
                            checked={rebillFormData.charge_back_to_drivers === "true"}
                            onChange={e =>
                              setRebillFormData(prev => ({
                                ...prev,
                                charge_back_to_drivers: e.target.checked ? "true" : "false",
                              }))
                            }
                          >
                            <Typography.Text strong>Charge Back to Drivers</Typography.Text>
                          </Checkbox>
                          <LabelInfo
                            title="By enabling this, the driver settlement will be reverted and added as adjustment for driver."
                            style={{ marginLeft: 4 }}
                          />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                          <Button
                            icon="close"
                            size="small"
                            onClick={closeRebillPopover}
                            disabled={isCreatingRebill}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            icon="save"
                            size="small"
                            loading={isCreatingRebill}
                            onClick={handleRebilling}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </Spin>
                  }
                  title={null}
                  trigger="click"
                  visible={isRebillPopoverVisible}
                  onVisibleChange={visible => {
                    if (isCreatingRebill) return;
                    // Only respond when opening
                    if (visible) {
                      setIsRebillPopoverVisible(true);
                    }
                  }}
                  placement="bottomRight"
                >
                  <Button_v2
                    icon="money-collect"
                    key="rebill-order"
                    size="medium"
                    onClick={() => setIsRebillPopoverVisible(true)}
                    className="small-btn tealButton"
                    text="Rebill"
                    style={{ marginRight: -15 }}
                  />
                </Popover>
              ))}
            &nbsp;&nbsp;
            {editable && (
                <>
                  <Button_v2
                    icon="save"
                    key="edit-order-charges"
                    size="medium"
                    onClick={openRemarkModal}
                    className="small-btn raised"
                    text="Save Order Charges"
                    disabled={!hasPendingChanges || invalidCharges}
                  />
                  <Button_v2
                    icon="reload"
                    key="reset-editing"
                    size="medium"
                    onClick={handleResetEditing}
                    className="send-edi-btn"
                    // className="small-btn flat"
                    text="Reset"
                    disabled={rebillingProgress}
                  />
                </>
              )}
          </div>
        </div>

        <div className="right-order-details-panel__bill-total">
          <span className="right-order-details-panel__bill-total-label">BILL TOTAL</span>
          <span className="right-order-details-panel__bill-total-amount">
            ${billTotal.toFixed(2)}
          </span>
        </div>

        <div
          className="right-order-details-panel__charges"
          style={{
            height: infoMessages.length > 0 ? "calc(100vh - 514px)" : "calc(100vh - 390px)",
          }}
        >
          {sections.map(section => {
            const commonProps = {
              key: section.key,
              section,
              editable: editable,
              glCodesList: glCodesList || [],
              groupGlByWarehouseSetting: groupGlByWarehouseSetting,
              orderDetails: orderDetails,
              onValidationChange: handleSectionValidationChange,
            };

            if (section.key === "accessorial_charges") {
              return (
                <AccessorialChargeSection
                  {...commonProps}
                  accessorialList={accessorialList}
                  displayComponents={displayComponents}
                  orgComponents={orgComponents}
                  onAddAccessorialLine={(lineKey, line) =>
                    handleLineAdded(section.key, lineKey, line)
                  }
                  onEditAccessorialLine={(lineKey, line) =>
                    handleLineEdited(section.key, lineKey, line)
                  }
                  onDeleteAccessorialLine={(lineKey, line, wasNeverAdded) =>
                    handleLineDeleted(section.key, lineKey, line, wasNeverAdded)
                  }
                  currentOrg={currentOrg}
                  drivers={drivers}
                />
              );
            }

            return (
              <ChargeSection
                {...commonProps}
                onLineAdded={handleLineAdded}
                onLineEdited={handleLineEdited}
                onLineDeleted={handleLineDeleted}
              />
            );
          })}
        </div>

        {infoMessages.length > 0 && (
          <Alert
            type="error"
            showIcon
            message={
              <div>
                {infoMessages.map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
              </div>
            }
            className="right-order-details-panel__alert"
          />
        )}

        <Modal
          visible={isRemarkModalVisible}
          onCancel={closeRemarkModal}
          footer={null}
          closable={false}
          destroyOnClose
          width={460}
          className="update-remark-modal"
        >
          <Spin spinning={isConfirming} size="small" tip="Updating...">
            <div className="update-remark-modal__header">
              <span className="update-remark-modal__title">Update Remark</span>
              <Icon
                type="close"
                className="update-remark-modal__close-icon"
                onClick={closeRemarkModal}
              />
            </div>

            <div className="update-remark-modal__body">
              <p className="update-remark-modal__description">
                Please provide a remark for updating charges for this order.
              </p>

              <label className="update-remark-modal__label">
                Remark <span className="update-remark-modal__required">*</span>
              </label>
              <Input.TextArea
                ref={remarkInputRef}
                className="update-remark-modal__textarea"
                placeholder="Enter update remark"
                value={remark}
                maxLength={MAX_REMARK_LENGTH}
                autoSize={{ minRows: 4, maxRows: 4 }}
                onChange={e => setRemark(e.target.value)}
                onPressEnter={e => {
                  // Allow Shift+Enter for a new line
                  if (e.shiftKey) return;

                  e.preventDefault();

                  if (!remark.trim() || isConfirming) return;

                  handleConfirmAndSaveRemark();
                }}
              />
              <div className="update-remark-modal__char-count">
                {remark.length} / {MAX_REMARK_LENGTH}
              </div>
            </div>

            <div className="update-remark-modal__footer">
              <Button_v2
                key="cancel"
                size="medium"
                className="flat small-btn"
                onClick={closeRemarkModal}
                disabled={isConfirming}
                text="Cancel"
              />

              <Button_v2
                size="medium"
                className="small-btn raised"
                onClick={handleConfirmAndSaveRemark}
                loading={isConfirming}
                disabled={!remark.trim()}
                text="Confirm & Save"
              />
            </div>
          </Spin>
        </Modal>
      </div>
      </Spin>
    </div>
  );
  }
);

RightOrderDetailsPanel.displayName = "RightOrderDetailsPanel";

RightOrderDetailsPanel.propTypes = {
  order: PropTypes.object,
  orderDetails: PropTypes.object,
  chargesDetails: PropTypes.object,
  loading: PropTypes.bool,
  accountInvoiceId: PropTypes.string,
  isEditMode: PropTypes.bool,
  onEnableEditMode: PropTypes.func,
  accessorialList: PropTypes.array,
  displayComponents: PropTypes.array,
  orgComponents: PropTypes.array,
  refreshCharges: PropTypes.func,
  onPendingChangesChange: PropTypes.func,
  setHasInvalidCharges: PropTypes.func,
  drivers: PropTypes.array,
  isFetchingDetails: PropTypes.bool,
  onOrderAmountUpdated: PropTypes.func,
  onChargesSaved: PropTypes.func,
  setRebillingProgress: PropTypes.func,
  rebillingProgress: PropTypes.bool,
};

export default RightOrderDetailsPanel;
